const Marionette = require('lib/marionette')
const _ = require('lib/underscore')
const $ = require('lib/jquery')
const Button = require('module/button/button')
const DialogModel = require('./dialog_model')
const template = require('./dialog.html')
const styles = require('./dialog.less')
const CssTemplate = require('./dialog.tcss')
const CssBehavior = require('behavior/css')
const Escape = require('lib/escape')
const DisableScroll = require('lib/disablescroll')
const radio = require('lib/radio')
const getZindex = require('lib/zindex')
const log = require('log').create('dialog', 'view') // eslint-disable-line

const channel = radio.dialog

const DialogView = Marionette.View.extend({

    displayName: 'DialogView',

    styles,

    template,

    ui: {
        overlay: '.dialog-overlay',
        box: '.dialog-box',
        head: '.dialog-head',
        title: '.dialog-title',
        close: '.dialog-close',
        // assign this class to any element to have it close the dialog on click
        close_trigger: '.dialog-close-trigger',
        content: '.dialog-content',
        buttons: '.dialog-buttons',
    },

    events: {
        'click @ui.close': 'onClickClose',
        'click @ui.close_trigger': 'onClickClose',
        'click @ui.overlay': 'onClickOverlay',
    },

    modelEvents: {
        'change:title': 'onChangeTitle',
    },

     
    constructor: function constructor(options) {
        this.options = options || {}
        this.dialog_id = _.uniqueId('dialog')
        this.initializeOptions()
        this.model = new DialogModel(this.options)
        Marionette.View.apply(this, arguments)
    },

    initialize() {
        _.bindAll(this, 'activate', 'render', 'close', 'destroy')

        // give fadeout time to complete before destroying
        this.destroy = _.debounce(this.destroy, 100)

        // dialogs render and show themselves when created
        this.render()
    },

    initializeOptions() {
        _.defaults(this.options, {
            text: null,
            html: null,
            view: null,
            viewClass: null,
            viewOptions: {},
            title: null,
            className: null,
            close_button: true,
            close_tooltip: 'Close',
            close_on_escape: true,
            disable_scroll: true,
            show_overlay: true,
            click_overlay_to_close: true,
            glyph: false,
            top: null,
            width: null,
            height: null,
            buttons: null,
            button_labels: null,
        })

        if (this.options.buttons) {
            // check for options.button_labels
            // this allows alert/error/confirm dialogs to define labels
            // but inherit the rest of the button functionality
            // if options.button_labels is null, this does nothing
            _.each(this.options.button_labels, (text, index) => {
                this.options.buttons[index].text = text
            })

            // convert buttons to a collection
            this.options.buttons = new Button.List(this.options.buttons)
        }

        // have to expose this option on the view
        // so we can call _.result using the view as context
        this.click_overlay_to_close = this.options.click_overlay_to_close
    },

    behaviors() {
        const behaviors = {}

        behaviors.Css = {
            behaviorClass: CssBehavior,
            label: `dialog-${this.dialog_id}`,
            template: CssTemplate,
            templateData: {
                dialog: this.model,
            },
            templateContext() {
                return {
                    id: this.dialog_id,
                }
            },
        }

        return behaviors
    },

    onRender() {
        this.addClassNames()

        this.showContent()

        this.showButtons()

        this.show()
    },

    addClassNames() {
        const classes = [
            'layer',
            'dialog',
            this.dialog_id,
        ]

        if (this.model.get('title')) classes.push('has-title')
        if (! this.model.get('title')) classes.push('untitled')
        if (this.model.get('glyph')) classes.push('has-glyph')
        if (this.model.get('close_button')) classes.push('has-closebutton')

        this.$el.addClass(classes.join(' '))
    },

    onChangeTitle(child, title, options) {
        this.ui.title.text(title)
        // have to coerce title to a boolean for toggleClass
        // but we also have to invert it, so we only need a single exclamation point!
        this.$el.toggleClass('untitled', ! title)
    },

    showContent() {
        let content_view

        if (this.options.viewClass) {
            // instantiate the view class
            // eslint-disable-next-line new-cap
            content_view = new this.options.viewClass(this.options.viewOptions)
        } else if (this.options.view) {
            // a view instance was passed in, use that
            content_view = this.options.view
        }

        if (content_view) {
            if (content_view.isDestroyed()) {
                this.destroy()
                throw new Error('Attempting to show a destroyed view in a dialog')
            }

            // give the view a reference to the dialog
            // so it can make model changes, etc
            content_view.dialog = this

            // wrap the view's destroy method to trigger close and then destroy after a delay
            // this allows time for the dialog to fade out before the view is destroyed
            content_view.destroy = _.wrap(content_view.destroy, function wrap(destroy) {
                content_view.trigger('close')
                _.delay(_.bind(destroy, content_view), 100)
            })

            // listen for the close event
            this.listenTo(content_view, 'close', this.close)

            // convert the content node to a region and show the view in it
            this.addRegion('content', { el: this.ui.content })
            this.showChildView('content', content_view)

            // attach the content view for reference in other methods
            this.content_view = content_view
        } else if (this.options.html) {
            this.ui.content.html(this.options.html)
        } else if (this.options.text) {
            this.ui.content.text(this.options.text)
        } else {
            throw new Error('Dialog requires a text or html string, or a view class, or a view instance.')
        }
    },

    showButtons() {
        if (! this.options.buttons) return

        this.addRegion('buttons', { el: this.ui.buttons })

        this.button_list_view = new Button.ListView({
            collection: this.options.buttons,
        })

        const listener = this.content_view || this
        // pass any button events from the button view to the content view
        listener.listenTo(this.button_list_view, 'all', function eventProxy(event_name) {
            const prefix = event_name.split(':')[0]
            if (prefix === 'button') listener.triggerMethod.apply(listener, arguments)
        })

        this.showChildView('buttons', this.button_list_view)
    },

    show() {
        if (this.options.disable_scroll) {
            DisableScroll.on(this.el)
        }
        if (this.options.close_on_escape) {
            Escape.listen(this.cid, this.close)
        }

        this.$el.css('z-index', getZindex())
        $.layers.append(this.$el)

        $(document.activeElement).blur()

        if (_.isFunction(this.options.open)) {
            this.options.open.call(this)
        }

        _.defer(this.activate)

        channel.trigger('dialog:open')
    },

    activate() {
        this.$el.addClass('active')
        // this will also trigger attach on this.content_view, if it exists
        this.triggerMethod('attach')
    },
    deactivate() {
        this.$el.removeClass('active')
    },

    close() {
        if (this.isDestroyed()) return
        this.deactivate()
        this.destroy()
    },

    onBeforeDestroy() {
        if (_.isFunction(this.options.close)) {
            this.options.close.call(this)
        }
        if (this.options.close_on_escape) {
            Escape.unlisten(this.cid)
        }
        if (this.options.disable_scroll) {
            DisableScroll.off(this.el)
        }
        // tell the world that a dialog is closing
        channel.trigger('dialog:close')
    },

    onClickClose() {
        this.close()
    },

    onClickOverlay() {
        // options.click_overlay_to_close can be a function
        // if it returns false, clicking the overlay won't close
        // can be used to prevent accidentally closing the dialog if user has entered some text
        const close = _.result(this, 'click_overlay_to_close')
        if (close) this.close()
    },

})

module.exports = DialogView
